// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A component for displaying asset disposal properties.
 * This module provides a structured view of an asset's disposal
 * details, including status, date, price, and reason. It handles
 * formatting and fallback displays for missing data.
 *
 * Key features:
 * - Status display
 * - Date formatting
 * - Price formatting
 * - Missing data handling
 * - Grid layout
 */
import { Asset } from '@/asset/api/types';
import { AssetFunctionalStatusIndicator } from '@/asset/components/functional-status-indicator';
import { Currency } from '@/currency/api/types';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { Box, BoxProps, Typography } from '@mui/material';
import { isNil } from 'lodash-es';
import { DateTime } from 'luxon';

/**
 * Formats a disposal date into a localized medium-length date.
 * Returns null if date is missing or invalid.
 *
 * @param date - Disposal date to format
 * @returns Formatted date string or null
 */
function getDate(date: DateTime | undefined | null) {
  if (isNil(date)) {
    return null;
  }

  return date.toLocaleString({ dateStyle: 'medium' });
}

/**
 * Formats a disposal price in its own currency, or null when there is none.
 */
function getPrice(price: Currency | undefined | null) {
  if (isNil(price)) {
    return null;
  }

  return formatCurrencyValue(price);
}

/**
 * Gets the disposal reason string if available.
 * Returns null if reason is missing.
 *
 * @param reason - Reason string to process
 * @returns Reason string or null
 */
function getReason(reason: string | undefined | null) {
  if (isNil(reason)) {
    return null;
  }

  return reason;
}

/**
 * Props for configuring the AssetScrapProperties component.
 *
 * @interface Props
 */
interface Props extends Pick<BoxProps, 'sx'> {
  /**
   * The asset to display disposal properties for.
   * Must include scrap and functional status data.
   */
  asset: Asset;
}

/**
 * A component for displaying asset disposal properties.
 * Shows a grid of disposal details including status, date,
 * price, and reason. Handles missing data with appropriate
 * fallback displays and styling.
 *
 * Features:
 * - Status indicator
 * - Date formatting
 * - Price formatting
 * - Reason display
 * - Grid layout
 * - Missing data handling
 * - Consistent styling
 *
 * Usage example:
 * ```tsx
 * function AssetDetails({ asset }: Props) {
 *   return (
 *     <AssetScrapProperties
 *       asset={asset}
 *       sx={{ padding: 2 }}
 *     />
 *   );
 * }
 * ```
 */
export function AssetScrapProperties(props: Props) {
  // Format and prepare display values
  const date = getDate(props.asset.scrappedContext?.disposalDate);
  const dateLabel = date ?? 'Not set';
  const dateColor = date ? 'text.primary' : 'text.secondary';

  const price = getPrice(props.asset.scrappedContext?.disposalPrice);
  const priceLabel = price ?? 'Not set';
  const priceColor = price ? 'text.primary' : 'text.secondary';

  const reason = getReason(props.asset.scrappedContext?.disposalReason);
  const reasonLabel = reason ?? 'Not set';
  const reasonColor = reason ? 'text.primary' : 'text.secondary';

  return (
    <Box
      display="grid"
      gridTemplateColumns="auto 1fr"
      alignItems="center"
      alignContent="center"
      justifyItems="start"
      rowGap={1}
      columnGap={2}
      sx={props.sx}
    >
      <Typography variant="subtitle2">Status</Typography>
      <AssetFunctionalStatusIndicator
        variant="chip"
        tooltip={false}
        status={props.asset.functionalStatus}
      />
      <Typography variant="subtitle2">Disposal date</Typography>
      <Typography variant="body2" color={dateColor}>
        {dateLabel}
      </Typography>
      <Typography variant="subtitle2">Disposal price</Typography>
      <Typography variant="body2" color={priceColor}>
        {priceLabel}
      </Typography>
      <Typography variant="subtitle2">Disposal reason</Typography>
      <Typography variant="body2" color={reasonColor}>
        {reasonLabel}
      </Typography>
    </Box>
  );
}
